"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry, WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDropsLeaderboardPage } from "@/generated/models/ApiDropsLeaderboardPage";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "react-use";
import useCapacitor from "./useCapacitor";
export enum WaveDropsLeaderboardSort {
  RANK = "RANK",
  PRICE = "PRICE",
  RATING_PREDICTION = "RATING_PREDICTION",
  TREND = "TREND",
  MY_REALTIME_VOTE = "MY_REALTIME_VOTE",
  CREATED_AT = "CREATED_AT",
}
interface UseWaveDropsLeaderboardProps {
  readonly waveId: string;
  readonly sort?: WaveDropsLeaderboardSort | undefined;
  readonly pausePolling?: boolean | undefined;
  readonly curatedByGroupId?: string | undefined;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly priceCurrency?: string | undefined;
}
const SORT_DIRECTION_MAP: Record<WaveDropsLeaderboardSort, "ASC" | "DESC" | undefined> = {
  [WaveDropsLeaderboardSort.RANK]: undefined,
  [WaveDropsLeaderboardSort.PRICE]: undefined,
  [WaveDropsLeaderboardSort.RATING_PREDICTION]: "DESC",
  [WaveDropsLeaderboardSort.TREND]: "DESC",
  [WaveDropsLeaderboardSort.MY_REALTIME_VOTE]: undefined,
  [WaveDropsLeaderboardSort.CREATED_AT]: "DESC"
};
const POLLING_DELAY = 3000;
const ACTIVE_POLLING_INTERVAL = 5000;
const INACTIVE_POLLING_INTERVAL = 30000;
function useTabVisibility() {
  const $ = _c(2);
  const [isVisible, setIsVisible] = useState(!document.hidden);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleVisibilityChange = () => setIsVisible(!document.hidden);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isVisible;
}
export function useWaveDropsLeaderboard(t0) {
  const $ = _c(91);
  const {
    waveId,
    sort: t1,
    pausePolling: t2,
    curatedByGroupId,
    minPrice,
    maxPrice,
    priceCurrency
  } = t0;
  const sort = t1 === undefined ? WaveDropsLeaderboardSort.RANK : t1;
  const pausePolling = t2 === undefined ? false : t2;
  const {
    isCapacitor
  } = useCapacitor();
  const queryClient = useQueryClient();
  const [canPoll, setCanPoll] = useState(false);
  const isTabVisible = useTabVisibility();
  const pollingTimeoutRef = useRef(null);
  const sortDirection = SORT_DIRECTION_MAP[sort];
  let t3;
  if ($[0] !== curatedByGroupId) {
    t3 = curatedByGroupId?.trim() ?? undefined;
    $[0] = curatedByGroupId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedCuratedByGroupId = t3;
  let t4;
  if ($[2] !== priceCurrency) {
    t4 = priceCurrency?.trim() ?? undefined;
    $[2] = priceCurrency;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const normalizedPriceCurrency = t4;
  const normalizedMinPrice = typeof minPrice === "number" && Number.isFinite(minPrice) && minPrice >= 0 ? minPrice : undefined;
  const normalizedMaxPrice = typeof maxPrice === "number" && Number.isFinite(maxPrice) && maxPrice >= 0 ? maxPrice : undefined;
  let normalizedPriceLower = normalizedMinPrice;
  let normalizedPriceUpper = normalizedMaxPrice;
  if (typeof normalizedPriceLower === "number" && typeof normalizedPriceUpper === "number" && normalizedPriceLower > normalizedPriceUpper) {
    normalizedPriceLower = normalizedMaxPrice;
    normalizedPriceUpper = normalizedMinPrice;
  }
  let t5;
  if ($[4] !== normalizedPriceLower) {
    t5 = typeof normalizedPriceLower === "number" ? normalizedPriceLower.toString() : undefined;
    $[4] = normalizedPriceLower;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== normalizedPriceUpper) {
    t6 = typeof normalizedPriceUpper === "number" ? normalizedPriceUpper.toString() : undefined;
    $[6] = normalizedPriceUpper;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== normalizedPriceCurrency || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      normalizedPriceCurrency,
      normalizedPriceLower: t5,
      normalizedPriceUpper: t6
    };
    $[8] = normalizedPriceCurrency;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const canonicalPriceFilters = t7;
  const t8 = normalizedCuratedByGroupId ?? null;
  const t9 = canonicalPriceFilters.normalizedPriceLower ?? null;
  const t10 = canonicalPriceFilters.normalizedPriceUpper ?? null;
  const t11 = canonicalPriceFilters.normalizedPriceCurrency ?? null;
  let t12;
  if ($[12] !== sort || $[13] !== sortDirection || $[14] !== t10 || $[15] !== t11 || $[16] !== t8 || $[17] !== t9 || $[18] !== waveId) {
    t12 = [QueryKey.DROPS_LEADERBOARD, {
      waveId,
      page_size: WAVE_DROPS_PARAMS.limit,
      sort,
      sort_direction: sortDirection,
      curation_id: t8,
      min_price: t9,
      max_price: t10,
      price_currency: t11
    }];
    $[12] = sort;
    $[13] = sortDirection;
    $[14] = t10;
    $[15] = t11;
    $[16] = t8;
    $[17] = t9;
    $[18] = waveId;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const queryKey = t12 as const;
  let t13;
  if ($[20] !== canonicalPriceFilters.normalizedPriceCurrency || $[21] !== canonicalPriceFilters.normalizedPriceLower || $[22] !== canonicalPriceFilters.normalizedPriceUpper || $[23] !== normalizedCuratedByGroupId) {
    t13 = t14 => {
      const {
        pageParam,
        pageSize,
        targetSort,
        targetSortDirection
      } = t14;
      const params = {
        page_size: pageSize.toString(),
        sort: targetSort
      };
      if (targetSortDirection) {
        params.sort_direction = targetSortDirection;
      }
      if (typeof pageParam === "number") {
        params.page = `${pageParam}`;
      }
      if (normalizedCuratedByGroupId) {
        params.curation_id = normalizedCuratedByGroupId;
      }
      if (canonicalPriceFilters.normalizedPriceLower) {
        params.min_price = canonicalPriceFilters.normalizedPriceLower;
      }
      if (canonicalPriceFilters.normalizedPriceUpper) {
        params.max_price = canonicalPriceFilters.normalizedPriceUpper;
      }
      if (canonicalPriceFilters.normalizedPriceCurrency) {
        params.price_currency = canonicalPriceFilters.normalizedPriceCurrency;
      }
      return params;
    };
    $[20] = canonicalPriceFilters.normalizedPriceCurrency;
    $[21] = canonicalPriceFilters.normalizedPriceLower;
    $[22] = canonicalPriceFilters.normalizedPriceUpper;
    $[23] = normalizedCuratedByGroupId;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const buildLeaderboardParams = t13;
  let t14;
  if ($[25] !== buildLeaderboardParams || $[26] !== waveId) {
    t14 = async t15 => {
      const {
        pageParam: pageParam_0,
        pageSize: pageSize_0,
        targetSort: targetSort_0,
        targetSortDirection: targetSortDirection_0
      } = t15;
      return await commonApiFetch({
        endpoint: `waves/${waveId}/leaderboard`,
        params: buildLeaderboardParams({
          pageParam: pageParam_0,
          pageSize: pageSize_0,
          targetSort: targetSort_0,
          targetSortDirection: targetSortDirection_0
        })
      });
    };
    $[25] = buildLeaderboardParams;
    $[26] = waveId;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const fetchLeaderboardPage = t14;
  let t15;
  if ($[28] !== sort) {
    t15 = lastPage => {
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        const haveZeroVotes = lastPage.drops.some(_temp);
        if (haveZeroVotes) {
          return null;
        }
      }
      return lastPage.next ? lastPage.page + 1 : null;
    };
    $[28] = sort;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const getNextPageParam = t15;
  let t16;
  let t17;
  if ($[30] !== fetchLeaderboardPage || $[31] !== getNextPageParam || $[32] !== queryClient || $[33] !== queryKey || $[34] !== sort || $[35] !== sortDirection || $[36] !== waveId) {
    t16 = () => {
      if (!waveId) {
        return;
      }
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t18 => {
          const {
            pageParam: pageParam_1
          } = t18;
          return await fetchLeaderboardPage({
            pageParam: pageParam_1,
            pageSize: WAVE_DROPS_PARAMS.limit,
            targetSort: sort,
            targetSortDirection: sortDirection
          });
        },
        initialPageParam: null,
        getNextPageParam,
        pages: 1,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    t17 = [fetchLeaderboardPage, getNextPageParam, queryClient, queryKey, sort, sortDirection, waveId];
    $[30] = fetchLeaderboardPage;
    $[31] = getNextPageParam;
    $[32] = queryClient;
    $[33] = queryKey;
    $[34] = sort;
    $[35] = sortDirection;
    $[36] = waveId;
    $[37] = t16;
    $[38] = t17;
  } else {
    t16 = $[37];
    t17 = $[38];
  }
  useEffect(t16, t17);
  let t18;
  if ($[39] !== fetchLeaderboardPage || $[40] !== sort || $[41] !== sortDirection) {
    t18 = async t19 => {
      const {
        pageParam: pageParam_2
      } = t19;
      return await fetchLeaderboardPage({
        pageParam: pageParam_2,
        pageSize: WAVE_DROPS_PARAMS.limit,
        targetSort: sort,
        targetSortDirection: sortDirection
      });
    };
    $[39] = fetchLeaderboardPage;
    $[40] = sort;
    $[41] = sortDirection;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = !pausePolling && !!waveId;
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = getDefaultQueryRetry();
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== getNextPageParam || $[45] !== queryKey || $[46] !== t18 || $[47] !== t19) {
    t21 = {
      queryKey,
      queryFn: t18,
      initialPageParam: null,
      getNextPageParam,
      enabled: t19,
      staleTime: 60000,
      ...t20
    };
    $[44] = getNextPageParam;
    $[45] = queryKey;
    $[46] = t18;
    $[47] = t19;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t21);
  let t22;
  if ($[49] !== data || $[50] !== sort) {
    bb0: {
      if (!data?.pages) {
        let t23;
        if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
          t23 = [];
          $[52] = t23;
        } else {
          t23 = $[52];
        }
        t22 = t23;
        break bb0;
      }
      const mappedDrops = mapToExtendedDrops(data.pages.map(_temp2), []);
      const uniqueDrops = generateUniqueKeys(mappedDrops, []);
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        t22 = uniqueDrops.filter(_temp3);
        break bb0;
      }
      t22 = uniqueDrops;
    }
    $[49] = data;
    $[50] = sort;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  const drops = t22;
  const hasInitialized = !!data?.pages;
  let t23;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setCanPoll(true);
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== data) {
    t24 = [data];
    $[54] = data;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  useDebounce(t23, 10000, t24);
  let t25;
  if ($[56] !== drops) {
    t25 = drops.some(_temp4);
    $[56] = drops;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  const hasTempDrop = t25;
  const canAutoRefetch = isTabVisible && !hasTempDrop;
  let t26;
  if ($[58] !== drops) {
    t26 = pollingData => {
      if (pollingData.drops.length === 0 || drops.length === 0) {
        return false;
      }
      const latestPolledDrop = pollingData.drops[0];
      const newestExistingTimestamp = Math.max(...drops.map(_temp5));
      if (latestPolledDrop) {
        const polledCreatedAt = new Date(latestPolledDrop.created_at).getTime();
        return polledCreatedAt > newestExistingTimestamp;
      }
      return true;
    };
    $[58] = drops;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  const checkForNewDrops = t26;
  let t27;
  if ($[60] !== queryKey) {
    t27 = [...queryKey, "polling"];
    $[60] = queryKey;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== canAutoRefetch || $[63] !== checkForNewDrops || $[64] !== fetchLeaderboardPage || $[65] !== refetch) {
    t28 = async () => {
      const result = await fetchLeaderboardPage({
        pageParam: null,
        pageSize: 1,
        targetSort: WaveDropsLeaderboardSort.CREATED_AT,
        targetSortDirection: "DESC"
      });
      if (canAutoRefetch && checkForNewDrops(result)) {
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
        }
        pollingTimeoutRef.current = setTimeout(() => {
          refetch();
        }, POLLING_DELAY);
      }
      return result;
    };
    $[62] = canAutoRefetch;
    $[63] = checkForNewDrops;
    $[64] = fetchLeaderboardPage;
    $[65] = refetch;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  const t29 = canPoll && !pausePolling;
  const t30 = isTabVisible ? ACTIVE_POLLING_INTERVAL : INACTIVE_POLLING_INTERVAL;
  const t31 = !isCapacitor;
  let t32;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = getDefaultQueryRetry();
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== checkForNewDrops || $[69] !== t27 || $[70] !== t28 || $[71] !== t29 || $[72] !== t30 || $[73] !== t31) {
    t33 = {
      queryKey: t27,
      queryFn: t28,
      select: checkForNewDrops,
      enabled: t29,
      refetchInterval: t30,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: t31,
      ...t32
    };
    $[68] = checkForNewDrops;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
    $[72] = t30;
    $[73] = t31;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  const {
    data: t34
  } = useQuery(t33);
  const haveNewDrops = t34 === undefined ? false : t34;
  let t35;
  let t36;
  if ($[75] !== queryClient || $[76] !== waveId) {
    t35 = () => () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      queryClient.removeQueries({
        queryKey: [QueryKey.DROPS, {
          waveId
        }]
      });
    };
    t36 = [waveId, queryClient];
    $[75] = queryClient;
    $[76] = waveId;
    $[77] = t35;
    $[78] = t36;
  } else {
    t35 = $[77];
    t36 = $[78];
  }
  useEffect(t35, t36);
  let t37;
  if ($[79] !== fetchNextPage || $[80] !== hasNextPage) {
    t37 = async () => {
      if (hasNextPage) {
        await fetchNextPage();
      }
    };
    $[79] = fetchNextPage;
    $[80] = hasNextPage;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  const manualFetch = t37;
  const t38 = isFetching || !hasInitialized;
  let t39;
  if ($[82] !== drops || $[83] !== fetchNextPage || $[84] !== hasNextPage || $[85] !== haveNewDrops || $[86] !== isFetchingNextPage || $[87] !== manualFetch || $[88] !== refetch || $[89] !== t38) {
    t39 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching: t38,
      isFetchingNextPage,
      refetch,
      haveNewDrops,
      manualFetch
    };
    $[82] = drops;
    $[83] = fetchNextPage;
    $[84] = hasNextPage;
    $[85] = haveNewDrops;
    $[86] = isFetchingNextPage;
    $[87] = manualFetch;
    $[88] = refetch;
    $[89] = t38;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  return t39;
}
function _temp5(drop_2) {
  return new Date(drop_2.created_at).getTime();
}
function _temp4(drop_1) {
  return drop_1.id.startsWith("temp-");
}
function _temp3(drop_0) {
  return drop_0.context_profile_context?.rating !== 0;
}
function _temp2(page) {
  return {
    wave: page.wave,
    drops: page.drops
  };
}
function _temp(drop) {
  return drop.context_profile_context?.rating === 0;
}